/**
 * SearchInput Component - Natural language search input
 * TASK-270: Unified Search Interface
 */

import { useState, useRef, useCallback, KeyboardEvent } from 'react';
import { Search, X, Loader2, Sparkles } from 'lucide-react';
import { cn } from '../../lib/utils';

export interface SearchInputProps {
  onSearch: (query: string) => void;
  placeholder?: string;
  isSearching?: boolean;
  disabled?: boolean;
  className?: string;
  suggestions?: string[];
  onSuggestionClick?: (suggestion: string) => void;
  autoFocus?: boolean;
}

export function SearchInput({
  onSearch,
  placeholder = 'Ask anything about your clients, portfolios, or market data...',
  isSearching = false,
  disabled = false,
  className,
  suggestions = [],
  onSuggestionClick,
  autoFocus = false,
}: SearchInputProps) {
  const [query, setQuery] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = useCallback(() => {
    const trimmedQuery = query.trim();
    if (trimmedQuery && !isSearching && !disabled) {
      onSearch(trimmedQuery);
    }
  }, [query, isSearching, disabled, onSearch]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit]
  );

  const handleClear = useCallback(() => {
    setQuery('');
    inputRef.current?.focus();
  }, []);

  const handleSuggestionClick = useCallback(
    (suggestion: string) => {
      setQuery(suggestion);
      onSuggestionClick?.(suggestion);
      onSearch(suggestion);
    },
    [onSearch, onSuggestionClick]
  );

  return (
    <div className={cn('w-full', className)}>
      {/* Search Input */}
      <div
        className={cn(
          'relative flex items-center rounded-xl border bg-white shadow-sm transition-all duration-200',
          isFocused ? 'border-blue-500 ring-2 ring-blue-100' : 'border-gray-200',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
      >
        {/* Search Icon */}
        <div className="flex items-center justify-center pl-4">
          {isSearching ? (
            <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />
          ) : (
            <Search className="h-5 w-5 text-gray-400" />
          )}
        </div>

        {/* Input Field */}
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={placeholder}
          disabled={disabled || isSearching}
          autoFocus={autoFocus}
          className={cn(
            'flex-1 py-4 px-3 text-gray-900 placeholder-gray-400',
            'bg-transparent border-none outline-none',
            'text-base',
            disabled && 'cursor-not-allowed'
          )}
        />

        {/* Clear Button */}
        {query && !isSearching && (
          <button
            type="button"
            onClick={handleClear}
            className="p-2 mr-1 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        )}

        {/* Submit Button */}
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!query.trim() || isSearching || disabled}
          className={cn(
            'flex items-center gap-2 px-4 py-2 mr-2 rounded-lg font-medium transition-all',
            query.trim() && !isSearching && !disabled
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
          )}
        >
          <Sparkles className="h-4 w-4" />
          <span className="hidden sm:inline">Search</span>
        </button>
      </div>

      {/* Suggestions */}
      {suggestions.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {suggestions.map((suggestion, index) => (
            <button
              key={index}
              type="button"
              onClick={() => handleSuggestionClick(suggestion)}
              disabled={isSearching || disabled}
              className={cn(
                'px-3 py-1.5 text-sm rounded-full border transition-all',
                'bg-gray-50 border-gray-200 text-gray-600',
                'hover:bg-blue-50 hover:border-blue-200 hover:text-blue-700',
                (isSearching || disabled) && 'opacity-50 cursor-not-allowed'
              )}
            >
              {suggestion}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default SearchInput;
